import makeStyles from '@material-ui/core/styles/makeStyles'
import { Box, Typography, Button, Chip } from '@material-ui/core'
import { SellOutlined, Sell } from '@mui/icons-material'
import { useMemo, useState, useEffect } from 'react'
import MemberFavoriteButton from 'photo-module/photo/ui/photo-viewer/actions-bar/MemberFavoriteButton'
import SpypointVideoHdButton from '../../../video-module/core/SpypointVideoHdButton'
import PhotoHdButton from './photo-viewer/full-hd-status/PhotoHdButton'
import useSelectedPhoto from 'photo-module/photo/core/useSelectedPhoto'
import videoState from 'video-module/video-clip/core/videoState'
import { useTranslation } from 'react-i18next'
import useSupportedFilters from 'photo-module/filters/core/useSupportedFilters'
import useFilterCollection from 'photo-module/filters-bar/core/filters.hooks'
import { useLDFlags } from 'spypoint/src/launchDarkly-module/launchDarklyFlagsContext'
import SpypointPhotoTaggingModal from './SpypointPhotoTaggingModal'
import { useLocation, useHistory } from 'react-router-dom'

export const useStyles = makeStyles(theme => ({
  viewerActionsBar: {
    width: '320px',
    height: '78vh',
    zIndex: 999,
    display: 'flex',
    margin: '20px',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: 'auto',
      margin: 0,
      flexDirection: 'column',
    },
  },
  photoStatusActionsContainer: {
    backgroundColor: '#313030',
    height: '80px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '5px',
    flexShrink: 0,
    gap: '8px',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-end',
      paddingRight: 15,
      gap: '8px',
      height: '50px',
      backgroundColor: '#000',
      marginBottom: 0,
    },
    '& button': {
      height: '48px !important',
      minWidth: '48px !important',
      minHeight: '48px !important',
      maxHeight: '48px !important',
      padding: '8px !important',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '4px',
    },
    '& svg': {
      '& path': {
        fill: theme.palette.secondary.contrastText,
      },
      height: '25px',
      width: '25px',
    },
  },
  editPanelContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#313030',
    flex: 1,
    padding: '24px',
    gap: '14px',
    [theme.breakpoints.down('sm')]: {
      backgroundColor: '#000',
    },
  },
  tagsHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  tagsTitle: {
    fontFamily: 'Oswald-regular',
    fontSize: '21px',
    fontWeight: '500',
    [theme.breakpoints.down('sm')]: {
      color: '#939090',
    },
  },
  actionButton: {
    minWidth: '55px',
    height: '28px',
    textAlign: 'center',
    alignItems: 'center',
    color: '#939090',
    padding: '7px 16px',
    border: '#939090 1px solid',
    borderRadius: 5,
    cursor: 'pointer',
    fontFamily: 'oswald-normal',
    fontSize: 14,
    '&:hover': {
      textDecoration: 'none',
      backgroundColor: 'rgba(245, 244, 244, 0.08 )',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      backgroundColor: '#D3CFCF',
      color: '#464444',
      opacity: 0.3,
    },
  },
  no_tags_container: {
    display: 'flex',
    justifyContent: 'center',
    gap: '5px',
  },
  no_tags: {
    fontFamily: 'ibm-plex-sans-regular',
    fontSize: '14px',
    LineWeight: '150%',
    color: '#D3CFCF',
  },
  disabledButton: {
    color: '#464444 !important',
    cursor: 'not-allowed',
    opacity: 0.3,
    backgroundColor: '#D3CFCF !important',
  },
  disabledActionButton: {
    opacity: '0.3 !important',
    cursor: 'not-allowed !important',
    pointerEvents: 'none !important',
    '& svg': {
      '& path': {
        fill: 'rgba(49, 48, 48, 1)? !important',
      },
    },
  },
  tagsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '10px',
    overflowX: 'hidden',
    overflowY: 'auto',
    maxHeight: '200px',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#464444',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#D3CFCF',
      borderRadius: '3px',
      '&:hover': {
        background: '#F5F4F4',
      },
    },
    scrollbarWidth: 'thin',
    scrollbarColor: '#D3CFCF #464444',
  },
  tagChip: {
    backgroundColor: '#F5F4F4',
    color: '#0E0B0B',
    fontFamily: 'Oswald-regular',
    fontSize: '14px',
    fontWeight: '400',
    width: 'fit-content',
    minWidth: 'max-content',
    height: '28px',
    borderRadius: '4px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#B1AFAF',
    padding: '0px',
    justifyContent: 'center',
    alignItems: 'center',
  },
}))

const SpypointViewerPanel = () => {
  const classes = useStyles()
  const { flags } = useLDFlags()
  const isMediaTagsEnabled = flags && flags['media-tags']
  const photo = useSelectedPhoto()
  const { t } = useTranslation()
  const shared = JSON.parse(localStorage.getItem('isShared'))
  const supportedFilters = useSupportedFilters()
  const { filterCollection, customTags, fetchFilters, fetchCustomTags } = useFilterCollection()
  const photoHasPreview = videoState.hasPreview(photo)
  const [isTaggingModalOpen, setIsTaggingModalOpen] = useState(false)
  const location = useLocation()
  const history = useHistory()

  const searchParams = new URLSearchParams(location.search)
  const shouldOpenModal = searchParams.get('openTagModal') === 'true' && isMediaTagsEnabled && !shared

  useEffect(() => {
    if (shouldOpenModal) {
      setIsTaggingModalOpen(true)
      // Clean up URL parameter after opening modal
      const newSearchParams = new URLSearchParams(location.search)
      newSearchParams.delete('openTagModal')
      const newSearch = newSearchParams.toString()
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`
      history.replace(newUrl)
    }
  }, [shouldOpenModal, location.pathname, location.search, history])

  if (!filterCollection?.species) {
    fetchFilters()
  }

  if (!customTags) {
    fetchCustomTags()
  }

  const filteredTags = useMemo(() => {
    const result = []

    if (photo.tags && filterCollection?.species) {
      const speciesNameIds = new Set(filterCollection.species.map(species => species.nameId))
      const speciesTags = photo.tags.filter(tag => speciesNameIds.has(tag))
      result.push(...speciesTags)
    }

    if (photo.customTags && Array.isArray(photo.customTags)) {
      const photoCustomTagNames = photo.customTags.map(tag => tag.name || tag.id || tag)
      result.push(...photoCustomTagNames)
    }

    if (photo.tags && customTags && customTags.length > 0) {
      const customTagIds = new Set(customTags.map(tag => tag.id || tag.name))
      const photoCustomTags = photo.tags.filter(tag => customTagIds.has(tag))
      result.push(...photoCustomTags)
    }

    return result
  }, [photo.tags, photo.customTags, filterCollection?.species, customTags])

  const getIconForTag = (tag) => {
    if (supportedFilters) {
      const supportedFilter = supportedFilters.find(filter => filter.filter === tag)
      if (supportedFilter?.icon) {
        return supportedFilter.icon
      }
    }

    if (filterCollection?.species) {
      const species = filterCollection.species.find(s => s.nameId === tag)
      if (species?.iconUrl) {
        return species.iconUrl
      }
    }

    return null
  }

  const handleOpenTaggingModal = () => {
    if (!shared) {
      setIsTaggingModalOpen(true)
    }
  }

  const handleCloseTaggingModal = () => {
    setIsTaggingModalOpen(false)
  }

  const truncateText = (text, maxLength = 36) => {
    if (text.length <= maxLength) {
      return text
    }
    return text.substring(0, maxLength - 3) + '...'
  }

  return (
    <Box className={classes.viewerActionsBar}>
      <Box className={classes.photoStatusActionsContainer}>
        { !!isMediaTagsEnabled && (
          <Button
            disabled={shared}
            onClick={handleOpenTaggingModal}
            data-testid="tag-modal-button"
            className={shared ? classes.disabledActionButton : ''}
          >
            { !filteredTags.length
              ? <SellOutlined />
              : <Sell /> }
          </Button>
        ) }
        <Box className={shared ? classes.disabledActionButton : ''}>
          { photoHasPreview ? <SpypointVideoHdButton photo={photo} hideText /> : <PhotoHdButton /> }
        </Box>
        <Box className={shared ? classes.disabledActionButton : ''}>
          <MemberFavoriteButton />
        </Box>
      </Box>
      { !isTaggingModalOpen
        ? (
          <Box className={classes.editPanelContainer}>
            <Box className={classes.tagsHeader}>
              <Typography className={classes.tagsTitle}>{ t('app:filters.tags.custom_tags.title').toUpperCase() }</Typography>
              { isMediaTagsEnabled && (
                <Button
                  className={`${classes.actionButton} ${shared ? classes.disabledButton : ''}`}
                  onClick={handleOpenTaggingModal}
                  disabled={shared}
                  data-testid="add-edit-tags-button"
                >
                  { !filteredTags.length ? t('app:filters.tags.custom_tags.add').toUpperCase() : t('app:filters.tags.custom_tags.edit').toUpperCase() }
                </Button>
              ) }
            </Box>
            { !filteredTags.length && (
              <Box className={classes.no_tags_container}>
                <Typography className={classes.no_tags}>{ t('app:filters.tags.custom_tags.no_tags') }</Typography>
              </Box>
            ) }
            <Box className={classes.tagsContainer}>
              { filteredTags.map((tag) => {
                const iconUrl = getIconForTag(tag)

                const isCustomTag = photo.customTags && photo.customTags.some(customTag => {
                  const customTagIdentifier = customTag.name || customTag.id || customTag
                  return customTagIdentifier === tag
                })

                const customTagObj = isCustomTag && photo.customTags
                  ? photo.customTags.find(customTag => {
                    const customTagIdentifier = customTag.name || customTag.id || customTag
                    return customTagIdentifier === tag
                  })
                  : null

                const displayLabel = isCustomTag && customTagObj
                  ? (customTagObj.name || customTagObj.label || tag)
                  : t('app:filters.tags.' + tag)

                const truncatedLabel = truncateText(displayLabel.toUpperCase())

                return (
                  <Chip
                    key={tag}
                    icon={iconUrl ? <img src={iconUrl} alt={tag} height={20} width={20} style={{ objectFit: 'contain' }} /> : undefined}
                    label={truncatedLabel}
                    className={classes.tagChip}
                  />
                )
              }) }
            </Box>
          </Box>
          )
        : (
          <SpypointPhotoTaggingModal
            onClose={handleCloseTaggingModal}
            currentTags={filteredTags}
            photo={photo}
            shared={shared}
            data-testid="species-tagging-modal"
          />
          ) }
    </Box>
  )
}

export default SpypointViewerPanel
